/**
 * /onboarding — the second half of signup: a verified, authenticated
 * identity with no tenant yet names their organization. Mounted behind
 * <ProtectedRoute /> (no role restriction — reachable by any authenticated
 * identity, tenant or not; see App.tsx). POST /auth/create-organization
 * 403s if the identity isn't verified yet and 409s if it already has one —
 * both surface inline rather than crashing, since either can legitimately
 * happen (e.g. a verified user manually revisiting this URL).
 *
 * Submitting files a REQUEST, not a real organization — a super admin
 * reviews it before any tenant/database is provisioned (see
 * auth.service.ts#requestTenant on the backend). On success this navigates
 * to /onboarding/pending rather than a dashboard.
 *
 * On mount this checks for an existing request, but ONLY a still-'pending'
 * one sends them to the pending screen. A REJECTED request must fall
 * through to the form — otherwise "Submit a new request" on the pending
 * page bounces straight back here and then back again, an infinite loop
 * that made that CTA permanently dead. A rejected request's name is
 * pre-filled so resubmitting doesn't mean retyping (matching Stripe
 * Connect's rejected-account resubmission flow). The backend already
 * permits this: requestTenant's guards and TenantRequest's partial-unique
 * indexes are both scoped to status:'pending', so a rejected request never
 * blocks a new one.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Building2 } from 'lucide-react';
import { ThemeCtx, useTheme, useThemeState, THEMES, NavBar, Footer } from '../home/marketing-shell';
import { useAuth } from '@/hooks/useAuth';

function OnboardingForm() {
  const { theme } = useTheme();
  const t = THEMES[theme];
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const { createOrganization, getOrganizationRequestStatus, user, loading, logout } = useAuth();

  const [organizationName, setOrganizationName] = useState('');
  const [error, setError] = useState('');
  // Distinguishes "checking for an existing request" (nothing rendered yet,
  // avoids a form flash before the redirect) from "confirmed none exists,
  // safe to show the form."
  const [checkingExisting, setCheckingExisting] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await getOrganizationRequestStatus();
      if (cancelled) return;
      // Only a LIVE request belongs on the pending screen. 'rejected' falls
      // through to the form (see this file's header comment), and 'approved'
      // means a tenant now exists — ProtectedRoute/login routing takes them
      // to the dashboard, so there's nothing to do here either.
      if (result.success && result.request?.status === 'pending') {
        navigate('/onboarding/pending', { replace: true });
        return;
      }
      if (result.success && result.request?.status === 'rejected') {
        setOrganizationName(result.request.organizationName);
      }
      setCheckingExisting(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!organizationName.trim()) {
      setError('Workspace name is required');
      return;
    }
    const result = await createOrganization({ organizationName: organizationName.trim() });
    if (result.success) {
      navigate('/onboarding/pending', { replace: true });
    } else {
      setError(result.error || 'Could not create your workspace');
    }
  }

  if (checkingExisting) return null;

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '11px 14px 11px 40px', borderRadius: 10, background: t.muted,
    border: `1px solid ${t.border}`, color: t.fg, fontSize: 14, fontFamily: "'Inter', sans-serif", outline: 'none',
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        background: t.card, border: `1px solid ${t.border}`, borderRadius: 24, padding: '40px 32px',
        display: 'flex', flexDirection: 'column', gap: 18, width: '100%', maxWidth: 440,
      }}
    >
      <div style={{ textAlign: 'center', marginBottom: 4 }}>
        <div style={{
          width: 52, height: 52, borderRadius: 14, background: t.greenDim, border: `1px solid ${t.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
        }}>
          <Building2 size={24} color={t.green} />
        </div>
        <h1 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 24, color: t.fg, marginBottom: 6 }}>
          {user?.firstName ? `Welcome, ${user.firstName}!` : "You're verified!"}
        </h1>
        <p style={{ color: t.mutedFg, fontSize: 14.5 }}>What should we call your workspace?</p>
      </div>

      <div>
        <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: t.mutedFg, marginBottom: 6 }}>
          Workspace name
        </label>
        <div style={{ position: 'relative' }}>
          <Building2 size={15} color={t.mutedFg} style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)' }} />
          <input
            style={inputStyle}
            value={organizationName}
            onChange={(e) => setOrganizationName(e.target.value)}
            placeholder="Acme Inc."
            autoFocus
            onFocus={(e) => { e.currentTarget.style.borderColor = t.green; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = t.border; }}
          />
        </div>
      </div>

      {error && (
        <div style={{ color: '#ef4444', fontSize: 13, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, padding: '10px 14px' }}>
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          padding: '14px 24px', borderRadius: 12, border: 'none', cursor: loading ? 'default' : 'pointer',
          background: t.green, color: isDark ? '#050C09' : '#fff', fontWeight: 800, fontSize: 15,
          fontFamily: 'Manrope, sans-serif', opacity: loading ? 0.7 : 1,
          boxShadow: `0 8px 30px ${isDark ? 'rgba(61,220,132,0.35)' : 'rgba(37,211,102,0.4)'}`,
        }}
      >
        {loading ? 'Submitting...' : 'Request workspace'} {!loading && <ArrowRight size={16} />}
      </button>

      {/* Already signed in here (identity-only session, no tenant yet) —
          NavBar's "Sign in" link can't help: Login.tsx auto-redirects any
          authenticated-but-tenant-less visitor straight back to
          /onboarding, so it never shows a form. Logging out first is the
          only way to actually sign in as a different account. */}
      <p style={{ textAlign: 'center', fontSize: 13, color: t.mutedFg }}>
        Wrong account?{' '}
        <button
          type="button"
          onClick={logout}
          style={{
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            color: t.green, fontWeight: 700, fontSize: 13, fontFamily: "'Inter', sans-serif",
            textDecoration: 'underline',
          }}
        >
          Log out
        </button>
      </p>
    </form>
  );
}

export default function Onboarding() {
  const themeCtxValue = useThemeState('light');
  const t = THEMES[themeCtxValue.theme];

  return (
    <ThemeCtx.Provider value={themeCtxValue}>
      <div style={{ minHeight: '100vh', background: t.bg, color: t.fg, fontFamily: "'Inter', sans-serif" }}>
        <NavBar />
        <main style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '130px 24px 80px' }}>
          <OnboardingForm />
        </main>
        <Footer />
      </div>
    </ThemeCtx.Provider>
  );
}
